import React, { useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { calcRRMarks } from "../../../../../graphics/graph-funcs";
import { chooseCmpxGraphMainAdapter } from "./choose-cmpx-graph-main-adapter";

import VisualSettings from "./VisualizatorSettingsMain.json";

const graphWrapStyle = {
    height: "100%",
    width: "100%",
    position: "relative",
    backgroundColor: "white"
};

export const ChooseCmpxGraphMain = forwardRef((props, ref) => {
    const canvasWrap = useRef();
    const isMounted = useRef(false);
    const hasInitCursor = useRef(false);
    const graphAdapter = useRef(null);
    const selectRectsAreVisible = useRef(false);
    const onFirstComplexColorChange = useRef(false);
    const { signalData, cursorPosition, fd, setMainGraphWidth, start_pnt, refreshFlag, userSweepUnits } = props;

    useEffect(() => {
        const mount = async () => {
            const { setMainGraphWidth, setCursorPosition, scale, signalLength } = props;
            graphAdapter.current = new chooseCmpxGraphMainAdapter({
                canvasRef: canvasWrap.current,
                visualSettings: VisualSettings,
                cursorCallback: setCursorPosition,
                fd: fd,
                signalLength: signalLength
            });

            graphAdapter.current.changeDefaultScaleSettings(scale);
            graphAdapter.current.changeDefaultFrequencySettungs(fd);
            await graphAdapter.current.initializeGraph();
            await graphAdapter.current.setupCursors();
            await graphAdapter.current.setupMarks();
            await graphAdapter.current.setupRectangles();
            graphAdapter.current.setUserSweepUnits(userSweepUnits);

            setMainGraphWidth(graphAdapter.current.getWidthInPoints(fd));
            graphAdapter.current.onResize(onResize);

            isMounted.current = true;
        };

        mount();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const update = async () => {
            await updateGraph();
        };

        update();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signalData, refreshFlag]);

    useEffect(() => {
        if (cursorPosition) {
            if (!hasInitCursor.current) {
                graphAdapter.current.moveCursors(cursorPosition);
                hasInitCursor.current = true;
            }
            updateHoverRectangles();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [start_pnt]);

    useEffect(() => {
        if (isMounted.current) {
            graphAdapter.current.setUserSweepUnits(userSweepUnits);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSweepUnits]);

    const updateGraph = async () => {
        if (signalData.length) {
            await Promise.all([graphAdapter.current.clearMarks(), graphAdapter.current.clearGraph()]);
            await graphAdapter.current.plotData(signalData);
            await Promise.all([updateMarks(), updateHoverRectangles(), updateSelectRectangles()]);
        }
    };

    const updateMarks = async () => {
        const { peaks } = props;
        const { marks: marksRR } = calcRRMarks(peaks[0], fd, 5);

        const convertedMarks = [
            ...graphAdapter.current.convertQRSMarksToStructure(peaks.flat()),
            ...graphAdapter.current.convertRRMarksToStructure(marksRR)
        ];
        graphAdapter.current.plotMarks(convertedMarks);
    };

    const updateHoverRectangles = async () => {
        const { isFirstComplex, start_pnt, end_pnt } = props;

        if (isFirstComplex) {
            if (!onFirstComplexColorChange.current) {
                graphAdapter.current.setHoverRectanglesFillColor("#88888850");
                onFirstComplexColorChange.current = true;
            }
        } else {
            if (onFirstComplexColorChange.current) {
                graphAdapter.current.setHoverRectanglesFillColor("#4dff6250");
                onFirstComplexColorChange.current = false;
            }
        }
        graphAdapter.current.moveHoverRectangles(start_pnt, end_pnt - start_pnt + 1);
    };

    const updateSelectRectangles = async () => {
        const { Q, S, signalStart } = props;

        const graphWIdth = graphAdapter.current.getWidthInPoints(fd);

        let start = Q - 0.25 * fd - signalStart; // Точка Q - 0.25 секунды
        let end = start + S + 0.25 * 2 * fd; // Точка S + 0.25 секунды
        start = Math.max(0, start);
        end = Math.min(graphWIdth, end);

        const width = end - start + 1;

        if (width <= 0) {
            if (selectRectsAreVisible.current) {
                graphAdapter.current.hideSelectRectangles(false);
                selectRectsAreVisible.current = false;
            }
        } else {
            if (!selectRectsAreVisible.current) {
                graphAdapter.current.hideSelectRectangles(true);
                selectRectsAreVisible.current = true;
            }
            graphAdapter.current.moveSelectRectangles(start, width);
        }
    };

    const onResize = () => {
        const { setRefreshFlag } = props;

        setMainGraphWidth(graphAdapter.current.getWidthInPoints(fd));
        setRefreshFlag(prev => prev + 1);
    };

    const changeScaleY = async type => {
        const index = await graphAdapter.current.changeScaleY(type);
        await graphAdapter.current.clearMarks();
        await updateMarks();

        return index;
    };

    const changeScaleX = type => {
        const index = graphAdapter.current.changeScaleX(type);
        const graphWidth = graphAdapter.current.getWidthInPoints(fd);
        setMainGraphWidth(graphWidth);

        return [index, graphWidth];
    };

    useImperativeHandle(ref, () => ({
        changeScaleY,
        changeScaleX
    }));

    return <div className="canvas-wrap" style={graphWrapStyle} ref={canvasWrap} />;
});
